import { Link } from '@tanstack/react-router';
import { useDroppable } from '@dnd-kit/core';
import type { OrcamentoResponse, ResumoStatusOrcamentos } from '@triade/contracts';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { ROTULO_STATUS } from '@/components/orcamento/tipos';
import { formatarContagem, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CartaoKanban, type CartaoKanbanProps } from './cartao-kanban';
import type { BuscaLista, StatusOrcamento } from './tipos';

const PONTO: Record<StatusOrcamento, string> = {
  RASCUNHO: 'bg-ink-disabled',
  ENVIADO: 'bg-blue-500',
  EM_NEGOCIACAO: 'bg-violet-500',
  APROVADO: 'bg-success',
  PERDIDO: 'bg-destructive',
};

const SEM_ORCAMENTO: Record<StatusOrcamento, string> = {
  RASCUNHO: 'em rascunho',
  ENVIADO: 'enviado',
  EM_NEGOCIACAO: 'em negociação',
  APROVADO: 'aprovado',
  PERDIDO: 'perdido',
};

export interface ColunaKanbanProps extends Pick<CartaoKanbanProps, 'nomeTabela' | 'nomeCondicao' | 'onMover' | 'podeMover' | 'agora'> {
  status: StatusOrcamento;
  /** Agregado da coluna (GET /orcamentos/resumo): contagem e soma. */
  resumo: ResumoStatusOrcamentos | undefined;
  /** Cartoes (GET /orcamentos?status=). */
  orcamentos: OrcamentoResponse[] | undefined;
  total: number | undefined;
  carregando: boolean;
  erro: Error | null;
  onTentar: () => void;
  /** Status do cartao em arrasto: destaca as colunas que aceitam o destino. */
  arrastando: StatusOrcamento | null;
  aceita: boolean;
  /** Filtros em vigor, levados para "Ver todos". */
  busca: BuscaLista;
}

/** Coluna por situacao. Contagem e soma vem do resumo da API; so aceita soltar quando a transicao existe na API. */
export function ColunaKanban({ status, resumo, orcamentos, total, carregando, erro, onTentar, arrastando, aceita, busca, ...cartao }: ColunaKanbanProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status, disabled: !aceita });
  const emArrasto = arrastando !== null;
  return (
    <section
      ref={setNodeRef}
      aria-label={ROTULO_STATUS[status]}
      data-testid={`coluna-${status}`}
      data-aceita={emArrasto ? String(aceita) : undefined}
      className={cn(
        'flex min-h-[420px] flex-col gap-2.5 rounded-2xl bg-line-soft p-3 transition-colors',
        emArrasto && aceita && 'ring-2 ring-primary/40',
        emArrasto && aceita && isOver && 'bg-primary/10 ring-primary',
        emArrasto && !aceita && arrastando !== status && 'opacity-50',
      )}
    >
      <div className="flex items-center gap-2 px-1 py-0.5">
        <span aria-hidden="true" className={cn('size-2 rounded-full', PONTO[status])} />
        <h2 className="flex-1 text-body font-semibold">{ROTULO_STATUS[status]}</h2>
        <span data-testid={`qtd-${status}`} className="tabular text-caption text-ink-secondary">
          {resumo ? formatarContagem(resumo.quantidade) : '…'}
        </span>
      </div>
      <div data-testid={`soma-${status}`} className="tabular px-1 text-small text-ink-body">
        {resumo ? formatarMoeda(resumo.total) : '…'}
      </div>

      {erro ? (
        <ErroFormacao erro={erro} onTentar={onTentar} />
      ) : carregando && !orcamentos ? (
        <p role="status" className="px-1 py-4 text-small text-ink-muted">
          Carregando…
        </p>
      ) : orcamentos && orcamentos.length === 0 ? (
        <p className="rounded-xl border border-dashed border-input px-3 py-6 text-center text-small text-ink-muted">Nenhum orçamento {SEM_ORCAMENTO[status]}.</p>
      ) : (
        orcamentos?.map((o) => <CartaoKanban key={o.id} orcamento={o} {...cartao} />)
      )}

      {orcamentos && total !== undefined && total > orcamentos.length && (
        <Link
          to="/vender/orcamentos"
          search={{ ...busca, status, pagina: undefined }}
          className="px-1 pt-1 text-small font-medium text-primary hover:underline"
        >
          Mostrando {formatarContagem(orcamentos.length)} de {formatarContagem(total)} · ver todos
        </Link>
      )}
    </section>
  );
}
